import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PageWithHeadline from '@/components/layout/PageWithHeadline';
import { useUser } from '@/app/providers';
import { useAssignDevices, useInvalidateExistingAssignments, useInvalidateAssignableDevices } from './hooks';
import { SelectDevicesStep, SelectTargetCompanyStep, ReviewConfirmStep } from './components';
import { DeviceAssignmentMobile, DeviceAssignmentTablet, DeviceAssignmentDesktop } from './components';
import type { AssignableDevice, TargetCompany, Step, StepId } from './types';

export function DeviceAssignmentPage() {
  const { t } = useTranslation();
  const user = useUser();
  const userCompanyId = user.user?.companyUuid || '';

  const [activeStep, setActiveStep] = useState<StepId>('devices');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<string>>(new Set());
  const [selectedDevices, setSelectedDevices] = useState<Map<string, AssignableDevice>>(new Map());
  const [selectedTargetCompany, setSelectedTargetCompany] = useState<TargetCompany | null>(null);
  const [sourceCompanyId, setSourceCompanyId] = useState<string>(userCompanyId);
  const [isSaving, setIsSaving] = useState(false);

  const assignDevicesMutation = useAssignDevices();
  const invalidateExistingAssignments = useInvalidateExistingAssignments();
  const invalidateAssignableDevices = useInvalidateAssignableDevices();

  useEffect(() => {
    if (userCompanyId && !sourceCompanyId) {
      setSourceCompanyId(userCompanyId);
    }
  }, [userCompanyId, sourceCompanyId]);

  useEffect(() => {
    if (sourceCompanyId && selectedTargetCompany && sourceCompanyId === selectedTargetCompany.id) {
      setSelectedTargetCompany(null);
    }
  }, [sourceCompanyId, selectedTargetCompany]);

  const handleSelectionChange = useCallback(
    (ids: Set<string>, devices: Map<string, AssignableDevice>) => {
      setSelectedDeviceIds(ids);
      setSelectedDevices(devices);
    },
    []
  );

  const handleTargetCompanySelect = useCallback((company: TargetCompany | null) => {
    setSelectedTargetCompany(company);
    if (company) {
      setActiveStep('review');
    }
  }, []);

  const handleSourceCompanyChange = useCallback((companyId: string) => {
    setSourceCompanyId(companyId);
  }, []);

  const handleCancel = useCallback(() => {
    setSelectedDeviceIds(new Set());
    setSelectedDevices(new Map());
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedTargetCompany || selectedDeviceIds.size === 0) return;

    setIsSaving(true);
    try {
      await assignDevicesMutation.mutateAsync({
        deviceIds: Array.from(selectedDeviceIds),
        targetCompanyId: selectedTargetCompany.id,
      });
      setSelectedDeviceIds(new Set());
      setSelectedDevices(new Map());
      invalidateExistingAssignments();
      invalidateAssignableDevices();
    } catch (error) {
      console.error('Failed to assign devices:', error);
    } finally {
      setIsSaving(false);
    }
  }, [selectedDeviceIds, selectedTargetCompany, assignDevicesMutation]);

  const hasUnsavedChanges = selectedDeviceIds.size > 0;
  const canSave = selectedDeviceIds.size > 0 && selectedTargetCompany !== null;
  const showActionButtons = hasUnsavedChanges || selectedTargetCompany !== null;

  const steps: Step[] = useMemo(
    () => [
      {
        id: 'devices',
        titleKey: 'deviceAssignment.steps.devices',
        getSubtitle: () =>
          selectedDeviceIds.size > 0
            ? t('deviceAssignment.steps.devicesSelected', { count: selectedDeviceIds.size })
            : '',
      },
      {
        id: 'company',
        titleKey: 'deviceAssignment.steps.company',
        getSubtitle: () => selectedTargetCompany?.name || '',
      },
      {
        id: 'review',
        titleKey: 'deviceAssignment.steps.review',
        getSubtitle: () =>
          hasUnsavedChanges
            ? t('deviceAssignment.steps.unsavedChanges')
            : t('deviceAssignment.steps.noChanges'),
      },
    ],
    [selectedDeviceIds.size, selectedTargetCompany, hasUnsavedChanges, t]
  );

  const isStepDisabled = (stepId: StepId) => {
    if (stepId === 'review') {
      return selectedTargetCompany === null;
    }
    return false;
  };

  const renderStepContent = (stepId: StepId) => {
    switch (stepId) {
      case 'devices':
        return (
          <SelectDevicesStep
            userCompanyId={userCompanyId}
            selectedDeviceIds={selectedDeviceIds}
            selectedDevices={selectedDevices}
            onSelectionChange={handleSelectionChange}
            selectedTargetCompanyId={selectedTargetCompany?.id || null}
            onSourceCompanyChange={handleSourceCompanyChange}
            sourceCompanyId={sourceCompanyId}
          />
        );
      case 'company':
        return (
          <SelectTargetCompanyStep
            sourceCompanyId={sourceCompanyId}
            selectedCompany={selectedTargetCompany}
            onCompanySelect={handleTargetCompanySelect}
          />
        );
      case 'review':
        return (
          <ReviewConfirmStep
            selectedDevices={selectedDevices}
            targetCompany={selectedTargetCompany}
            onSave={handleSave}
            onCancel={handleCancel}
            isSaving={isSaving}
            canSave={canSave}
            showActionButtons={showActionButtons}
          />
        );
    }
  };

  return (
    <PageWithHeadline headlineKey="deviceAssignment.title">
      <div className="flex-1 min-h-0 flex flex-col">
        <DeviceAssignmentMobile
          steps={steps}
          activeStep={activeStep}
          onStepChange={setActiveStep}
          isStepDisabled={isStepDisabled}
          renderStepContent={renderStepContent}
        />

        <DeviceAssignmentTablet
          steps={steps}
          activeStep={activeStep}
          onStepChange={setActiveStep}
          isStepDisabled={isStepDisabled}
          renderStepContent={renderStepContent}
        />

        <DeviceAssignmentDesktop
          steps={steps}
          isStepDisabled={isStepDisabled}
          renderStepContent={renderStepContent}
        />
      </div>
    </PageWithHeadline>
  );
}

